/*
 * Copyright (c) 2021-2024. StockAnywhere CC
 * StockAnywhere - https://www.stockanywhere.io
 * View license agreement and terms of use at https://www.stockanywhere.io/legal
 *
 * @author  Juan <juan@datasmart.co.za>
 * @version 1.0
 * @updated 2024-11-28
 */

import { ChangeEvent, useEffect, useState } from "react";
import { Button, Card, CardBody, Container, Modal } from "react-bootstrap";
import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, Search, X } from "lucide-react";
import { CastType, IQuery, IWhere } from "./Interfaces";
import { useAuth } from "../../contexts/AuthContext";
import { formatDateFull } from "./utils/AppUtil";
import { JsonToHtmlTable } from "./utils/JsonObjToTableUtil";
import { useLocation, useNavigate } from 'react-router-dom';
import ShowErrorAlert from "../ShowErrorAlert";

interface INotification {
    
    "user.id": string | null,
    "user.user_name": string | null,
    "user.first_name": string | null,
    "user.last_name": string | null,
    "user.email": string | null,
    "user.cellphone": string | null,
    "user.created": string | null,
    "user.updated": string | null,

    id: string,
    repeat_count: number,
    reference: string | null,
    category: string | null,
    system: string | null,
    description: string | null,
    function_code: string | null,
    handled: boolean,
    handled_at: string | null,
    handled_comment: string | null,
    last_encountered: string | null,
    created: string | null,
    updated: string | null,
}

const notificationQuery: IQuery = {
    tableName: "dst_notification",
    select: [

    ],
    groupBy: [],
    join: [
        {
            "relationship": "user",
            "joinType": "LEFT"
        },
        {
            "relationship": "function",
            "joinType": "LEFT"
        }                
    ],
    orderBy: [ 
        {
            fullColumnName: "last_encountered",
            direction: "DESC"
            
        }
    ],
    where: [
        {
            "fullColumnName": "handled",
            "value": false,
            "operator": "EQ",
            "cast": CastType.BOOLEAN
        }, 
        {
            "fullColumnName": "infrastructure",
            "value": false,
            "operator": "EQ",
            "cast": CastType.BOOLEAN
        }               
    ],
    mainTableOnly: false,
    includeExtraColumns: true,
    includeControlColumns: true,
    modifiedAfter: null,
    touchedBefore: null,
    isDirty: null,
    pageNum: 0,
    pageSize: 100,
    frontEndOnly: null
} 

const repeatCountBgColour = (count: number): string => {

    if(count < 5) {
        return 'bg-primary'; 
    }

    if(count >= 5 && count < 30) {
        return 'bg-warning'; 
    }

    if(count >= 30) {
        return 'bg-danger'; 
    }

    return 'bg-primary';
}



export const Notification: React.FC = () => {

    //error_notification_template.xlsx
    //error_notification_report.xlsx
 

    const location = useLocation();
    const navigate = useNavigate(); 

    const [shipmentReference, setShipmentReference] = useState<string>(location.state?.shipment_num ?? "");
    const [errorCategory, setErrorCategory] = useState<string>(location.state?.category ?? "");

    const [notificationDescription, setNotificationDescription] = useState<string>(location.state?.description ?? "");
    const [notificationLastEncountered, setNotificationLastEncountered] = useState<string>(location.state?.last_encountered ?? "");

    const queryUrl: string = `${process.env.REACT_APP_BASE_URL}/${"query/data"}`;
    const queryExportReportXLSXFormatUrl: string = `${process.env.REACT_APP_BASE_URL}/${"reports/xlsx"}`;
    const { fetchData, showErrorAlert, responseErrorMessage, responseStatus  }  = useAuth();
    
    const [queryState, setQueryState] = useState<IQuery>(notificationQuery);
    const [notifications, setNotifications] = useState<INotification[]>([]);

    const [searchState, setSearchState] = useState<{ [key: string]: string }>({
        reference: "",
        category: "",
        system: "",
        function_code: "",
        description: "",
        created: "",
        last_encountered: "",
    });

    const [tableRows, setTableRows] = useState<React.ReactElement[]>([]);

    // Keep track of the currentlly selected record.
    const [currentRecordIndex, setCurrentRecordIndex] = useState<number>(0);

    // Handled?
    const [isHandled, setIsHandled] = useState(false);
    const [showSystemNotifications, setShowSystemNotifications] = useState<boolean>(false);
    // Modal Show & Close.
    const [showModal, setShowModal] = useState(false);

    const [hasGotShipmentReference, setHasGotShipmentReference] = useState<boolean>(false);
    const [isNotificationsFetched, setIsNotificationsFetched] = useState<boolean>(false);
    const [hasUsedInitialReference, setHasUsedInitialReference] = useState<boolean>(false);

    //Pagination Testing 
    const [pageNumber, setPageNumber] = useState<number>(0); 
    const [pageSize, setPageSize] = useState<number>(100);   
    const [totalRecords, setTotalRecords] = useState<number>(0);
    const [totalPages, setTotalPages] = useState<number>(0);

    const [downloading, setDownloading] = useState(false);
    const [downloadComplete, setDownloadComplete] = useState(false);
    const [shouldFadeOut, setShouldFadeOut] = useState(false);
    
    const [blockInitialFetch, setBlockInitialFetch] = useState<boolean>(!!location.state);

    // Default search state generator
    function defaultSearchState() {
        return {
            reference: "",
            category: "",
            system: "",
            function_code: "",
            description: "",
            created: "",
            last_encountered: "",
        };
    }

    // Set searchState from initial location.state
    useEffect(() => {
        if (!hasUsedInitialReference) {
         const timer = setTimeout(() => {
            if (shipmentReference) {
                // Case 1 or 2: Shipment reference (with or without category)
                setSearchState(prev => ({
                    ...prev,
                    reference: shipmentReference,
                    category: errorCategory,
                }));
            } else if (errorCategory && notificationDescription && notificationLastEncountered) {
                // Case 3: Category + Description + Last Encountered
                setSearchState(prev => ({
                    ...prev,
                    category: errorCategory,
                    description: notificationDescription,
                    last_encountered: notificationLastEncountered,
                }));
            }
            setHasGotShipmentReference(true);
            setHasUsedInitialReference(true);
        }, 1000);
        return () => clearTimeout(timer);
      }
    }, [ shipmentReference, errorCategory, notificationDescription, notificationLastEncountered, hasUsedInitialReference]); 


   useEffect(() => {
        if (!hasGotShipmentReference) return;

        const where: IWhere[] = [
            {
                fullColumnName: "handled",
                value: isHandled,
                operator: "EQ"
                
            }
        ];

        if (shipmentReference) {
            where.push({
                fullColumnName: "reference",
                value: `%${shipmentReference}%`,
                operator: "LIKE",
                cast: CastType.STRING
            });

            if (errorCategory) {
                where.push({
                    fullColumnName: "category",
                    value: `%${errorCategory}%`,
                    operator: "LIKE",
                    cast: CastType.STRING
                });
            }
        } else if (errorCategory && notificationDescription && notificationLastEncountered) {
            where.push(
                {
                    fullColumnName: "category",
                    value: `%${errorCategory}%`,
                    operator: "LIKE",
                    cast: CastType.STRING
                },
                {
                    fullColumnName: "description",
                    value: `%${notificationDescription}%`,
                    operator: "LIKE",
                    cast: CastType.STRING
                },
                {
                    fullColumnName: "last_encountered",
                    value:`%${notificationLastEncountered}%`,
                    operator: "LIKE",
                    cast: CastType.STRING
                }
            );
        }

        // Update query and unblock fetch
        setQueryState(prev => ({ ...prev, where }));
        setBlockInitialFetch(false);

        // Clear used filters
        setShipmentReference("");
        setErrorCategory("");
        setNotificationDescription("");
        setNotificationLastEncountered("");

    }, [hasGotShipmentReference, isHandled]);


    useEffect(() => {
        //console.log("queryState updated", queryState);
        if (blockInitialFetch) {
            //console.log("Blocked initial fetch");
            return;
        }

        //console.log("Fetching notifications");
        fetchNotifications();
    }, [queryState, blockInitialFetch]);

    useEffect(() => {
    const hasRouteState =
            location.state?.shipment_num ||
            (location.state?.category && location.state?.description && location.state?.last_encountered);

        if (!hasRouteState && !hasUsedInitialReference) {
            setSearchState(defaultSearchState());
            setQueryState(notificationQuery);
            setHasGotShipmentReference(false);
            setHasUsedInitialReference(true);
        }
    }, [location.state, hasUsedInitialReference]);

    // Clear location.state after use
    useEffect(() => {
        if (hasUsedInitialReference) {
            navigate(location.pathname, { replace: true });
        }
    }, [hasUsedInitialReference, location.pathname, navigate]);

    // Pagination updates
    useEffect(() => {
        setQueryState(prev => ({
            ...prev,
            pageNum: pageNumber,
            pageSize: pageSize,
        }));
    }, [pageNumber, pageSize]);

    useEffect(() => {
        if (downloadComplete && !downloading) {
            // Start fading after 4.5s
            const fadeTimer = setTimeout(() => {
                setShouldFadeOut(true);
            }, 4500);

            // Hide alert completely after 5s
            const hideTimer = setTimeout(() => {
                setDownloadComplete(false);
                setShouldFadeOut(false); // reset for future
            }, 5000);

            return () => {
                clearTimeout(fadeTimer);
                clearTimeout(hideTimer);
            };
        }
    }, [downloadComplete, downloading]);

    
    
    // Update the QueryState when the handled checkbox is changed
    const handleQueryHandledChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        const query = {...queryState}
        query.where.forEach((clause: IWhere, index) => {
            if(clause.fullColumnName === "handled"){
                setIsHandled(event.target.checked);
                clause.value = event.target.checked;
            }
        });
        setQueryState(query); 
    };

     // Update the QueryState when the show system notifications checkbox is changed
     const handleQuerySystemNotificationsChange = (event: React.ChangeEvent<HTMLInputElement>) => {

        const query = {...queryState}
        query.where.forEach((clause: IWhere, index) => {
            if(clause.fullColumnName === "infrastructure"){
                setShowSystemNotifications(event.target.checked);
                clause.value = event.target.checked;
            }
        });
        setQueryState(query); 
    };

    // Set the current record and show the record detail modal.
    const showDetail = (index: number) => {
        setCurrentRecordIndex(index);
        setShowModal(true);
    }
    
    // Build the Notification Table
    const buildTableRows = (data: any[]): React.ReactElement[] => {
        const rowElements: React.ReactElement[] = [];
    
        const cellStyle: React.CSSProperties = { border: '1px solid #deebed', whiteSpace: 'nowrap', padding: '5px 8px', fontSize: '0.75rem' };
    
        data.map((record, index) => {
            const colour = repeatCountBgColour(record.repeat_count);
    
            rowElements.push(
                <tr id={`${index}`} key={index}>
                    <td style={cellStyle} className={`${colour} text-white text-center`} title={record?.repeat_count}>{record?.repeat_count}</td>
                    <td style={{ ...cellStyle, cursor: "pointer", textDecoration: "underline" }} onClick={() => showDetail(index)}  title={record?.reference}>
                        {record?.reference || "-"}
                    </td>
                    <td style={cellStyle} title={record?.category} >{record?.category}</td>
                    <td style={cellStyle} title={record?.system}>{record?.system}</td>
                    <td style={cellStyle} title={record?.function_code}>{record?.function_code}</td>
                    <td style={{ ...cellStyle, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'}} title={record?.description}>
                    {record?.description}
                    </td>
                    <td style={cellStyle} title={record?.created}>{formatDateFull(record?.created)}</td>
                    <td style={cellStyle} title={record?.last_encountered}>{formatDateFull(record?.last_encountered)}</td>
    
                    <td style={cellStyle}>
                        {isHandled && (
                            <table className="table table-borderless">
                                <tbody>
                                    <tr>
                                        <td><b>Handled By:</b></td>
                                        <td>{record["user.user_name"]}</td>
                                    </tr>
                                    <tr>
                                        <td>{record["user.first_name"]}</td>
                                        <td>{record["user.last_name"]}</td>
                                    </tr>
                                    <tr>
                                        <td><b>Handled at:</b></td>
                                        <td>{formatDateFull(record["handled_at"])}</td>
                                    </tr>
                                    <tr>
                                        <td colSpan={2}>{record["handled_comment"]}</td>
                                    </tr>
                                </tbody>
                            </table>
                        )}
    
                        {!isHandled && (
                            <form
                                id={`${record?.id}`}
                                name={`${record?.id}`}
                                className="d-flex align-items-center"
                                method="POST"
                                action={`${process.env.REACT_APP_BASE_URL}/notifications/${record.id}/handle`}
                                onSubmit={(e) => e.preventDefault()}
                            >
                                <textarea
                                    id={`handled_comment-${record?.id}`}
                                    name={`handled_comment-${record?.id}`}
                                    className="form-control me-2"
                                    rows={1}
                                    placeholder="Comment..."
                                    style={{ width: '140px'}}
                                ></textarea>
    
                                <button
                                    type="button"
                                    className="btn btn-secondary"
                                    onClick={() => {
                                        const form = document.getElementById(`${record?.id}`) as HTMLFormElement | null;
                                        const textArea = document.getElementById(`handled_comment-${record?.id}`) as HTMLFormElement | null;
    
                                        if (form) {
                                            const formData = new FormData(form);
                                            const comment = formData.get(`handled_comment-${record?.id}`);
                                            fetchData(form.action, { method: "POST", body: JSON.stringify({ handledComment: comment }) }, fetchNotifications);
                                        }
    
                                        if (textArea) {
                                            textArea.value = "";
                                        }
                                    }}
                                >
                                    Handle
                                </button>
                            </form>
                        )}
                    </td>
                </tr>
            );
        });
    
        return rowElements;
    };
    

    const processFetch = (result: any) => {
        
        const data: INotification[] = [];

        for(const key in result.data){
            data.push({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
            //console.log({...result.data[key].data, ...result.data[key].control, ...result.data[key].undefined});
        }

        setNotifications(data);
        setIsNotificationsFetched(true);
        setTableRows(buildTableRows(data));
      
        // Pagination 
        setPageSize(pageSize);
        setTotalRecords(result?.numRecords);
        setTotalPages(Math.ceil(result?.numRecords / pageSize));
    }

    const fetchNotifications = () => {
        setNotifications([]);
        fetchData(queryUrl, {method:"POST", body: JSON.stringify(queryState)}, processFetch);
    }

    const handleSearchOnChange = (event: ChangeEvent<HTMLInputElement>) => {
        const { id, value } = event.target;
        //console.log(`${id} : ${value}`);
        setSearchState((prev) => ({ ...prev, [id]: value }));
    };

    // Function to handle date parsing and formatting
    function parseDate(value: string): string {
        let date: Date | null = null;

        // Try to parse the value in various formats
        const datePatterns = [
            /^\d{2}-\d{2}-\d{4}$/, // dd-mm-yyyy
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2} [APM]{2}$/, // dd-mm-yyyy hh:mm:ss AM/PM
            /^\d{2}-\d{2}-\d{4} \d{2}:\d{2}:\d{2}$/, // dd-mm-yyyy hh:mm:ss
            /^\d{4}-\d{2}-\d{2}$/, // yyyy-mm-dd (valid input)
        ];

        // Check if the value matches any of the patterns
        if (datePatterns[0].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [day, month, year] = value.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[1].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' '); 
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[2].test(value)) {
            // Format dd-mm-yyyy to yyyy-mm-dd
            const [datePart] = value.split(' ');
            const [day, month, year] = datePart.split('-');
            date = new Date(`${year}-${month}-${day}`);
        } else if (datePatterns[3].test(value)) {
            // Valid yyyy-mm-dd, no changes needed
            date = new Date(value);
        } else {
            // If no pattern matches, return the original value
            return value;
        }

        // If date is valid, format it as yyyy-mm-dd
        if (date && !isNaN(date.getTime())) {
            return date.toISOString().split('T')[0]; // Format as yyyy-mm-dd
        }

        // If invalid date, return the original value
        return value;
    }

    const searchClicked = () => {
        let where: IWhere[] = [];

        // Always include handled and infrastructure
        where.push(
            {
                fullColumnName: "handled",
                value: isHandled,
                operator: "EQ"
               
            },
            {
                fullColumnName: "infrastructure",
                value: showSystemNotifications,
                operator: "EQ"
               
            }
        );

        Object.entries(searchState).forEach(([key, value]) => {
            const trimmedValue = value.trim(); 

            if (key === "created" || key === "last_encountered") {
                if (trimmedValue) {
                    const formattedValue = parseDate(trimmedValue);
                    value = formattedValue;
                }
            }

            if (trimmedValue.length) {
                const clause: IWhere = {
                    fullColumnName: key,
                    value: '%' + trimmedValue + '%',
                    operator: "LIKE",
                    cast: CastType.STRING
                };
                where.push(clause);
            }
        });

        const query = { ...queryState };
        query.where = where;
        setQueryState(query);
        setPageNumber(0); // Reset page to first page
        setPageSize(100); // Reset page size to 100
    };

    const clearClicked = () => {

        setSearchState({
            reference: "",
            category: "",
            system: "",
            function_code: "",
            description: "",
            created: "",
            last_encountered: "",
        });

        let where: IWhere[] = [];
        where.push(
            {
                "fullColumnName": "handled",
                "value": isHandled,
                "operator": "EQ"
                
            },
            {
                "fullColumnName": "infrastructure",
                "value": showSystemNotifications,
                "operator": "EQ"
               
            }            
        ); 
        const query = {...queryState}
        query.where = where;
        setQueryState(query);
    }    


 
    // Pagination handlers
    const goToFirstPage = () => setPageNumber(0);
    const goToPreviousPage = () => setPageNumber(prev => prev - 1);
    const goToNextPage = () => setPageNumber(prev => prev + 1);
    const goToLastPage = () => setPageNumber(totalPages - 1);

    // Update page size handler
    const handlePageSizeChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
        const newSize = parseInt(event.target.value, 10);
        setPageSize(newSize);
        setPageNumber(0); // Reset to first page when changing page size
    };


    const handleExport = () => {

        setDownloading(true);
        setDownloadComplete(false);
    
        const template = "error_notification_template.xlsx"; 
        const report = "error_notification_report.xlsx"; 

        const urlWithParams = `${queryExportReportXLSXFormatUrl}?templateFilename=apps/ares/${template}&reportFilename=${report}`;

         // Clone queryState and remove paging
        const { pageNum, pageSize, ...exportQuery } = queryState;
        const requestBody = { query: exportQuery };

        fetchData(urlWithParams, 
        {method:"POST", body: JSON.stringify(requestBody)}, 
        (result: any, status: string) => {
            if (status === "200" || status === "201") {
                handleExcelExport(result);
                setDownloadComplete(true);  // Change banner to "Downloaded!"
                setDownloading(false);      // Hide "Downloading..."
            } else {
                setDownloading(false);      // Hide "Downloading..."
                setDownloadComplete(false); // Hide success banner if error
                // Optionally, show error banner here
            }
        });
    }


    const handleExcelExport = async (result: any) => {
            // Use the abbreviated name for both the template and report
            const fileName = `error_notification_report.xlsx`; // Use abbreviated table name

            // Create a download link for the Blob
            const url = URL.createObjectURL(result);
            // Create a temporary <a> element to trigger the download
            const a = document.createElement('a');
            a.href = url;
            a.download = fileName; // Set the default filename here
            document.body.appendChild(a);
            a.click();
    }


   const thStyle: React.CSSProperties = {
        border: '1px solid #deebed',
        color: "#6c757d",
        padding: "8px",
        whiteSpace: "nowrap" as const,
        backgroundColor: "#f8f9fa",
    };
       
    const thControlsStyle = {
        textWrap: 'nowrap',
        backgroundColor: 'white', 
        border: '1px solid #deebed',
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center',
        padding:'2px 0px',
        margin: '0px'
    };
    
    const thSearchStyle = {
        textWrap: 'nowrap',
        // backgroundColor: 'white', 
        backgroundColor: "#f8f9fa",
        border: '1px solid #deebed',
        padding: '8px 4px'
    };


    return (
        <Container fluid className="p-0">
             <>
               { showErrorAlert && <ShowErrorAlert message={responseErrorMessage} /> } 
              
               {/* Show only while downloading and not errored */}
                {downloading && !downloadComplete && !showErrorAlert && (
                  <div className="alert d-flex align-items-center py-2 px-3 mb-2" role="alert" style={{ backgroundColor: "#e6eefa", border: '1px solid rgb(222, 235, 237)', color: "#035a70" }}>
                    <span className="flex-grow-1 text-center">
                      Your report is currently being processed. Please wait...
                    </span>
                  </div>
                )}

                {/* Show success only if download succeeded */}
                {downloadComplete && !downloading && !showErrorAlert && (
                  <div className={`alert d-flex align-items-center py-2 px-3 mb-2 ${shouldFadeOut ? 'fading-out' : ''}`} role="alert" 
                      style={{ backgroundColor: "#c7f5bf", border: "1px solid rgb(222, 235, 237)", color: "#037012", transition: "opacity 0.5s ease-in-out", opacity: shouldFadeOut ? 0 : 1 }}>
                    <span className="flex-grow-1 text-center">
                      Your report has been successfully downloaded.
                    </span>
                    <button type="button" 
                      style={{ position: 'absolute', top: '50%', right: '10px', transform: 'translateY(-50%)', backgroundColor: '#c7f5bf', color: '#e53e3e', borderRadius: '8px', padding: '6px', height: '32px', width: '32px', display: 'inline-flex', justifyContent: 'center', alignItems: 'center', border: 'none', cursor: 'pointer', transition: 'background-color 0.2s ease' }}
                      aria-label="Close"
                      onClick={() => setDownloadComplete(false)} 
                    >
                      <svg style={{ width: '12px', height: '12px' }} aria-hidden="true" xmlns="http://www.w3.org/2000/svg"  fill="none" viewBox="0 0 14 14">
                        <path stroke="#037012" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6" />
                      </svg>
                    </button>
                  </div>
                )}
            </>
            <Card>
                 <Card.Header>
                    <div className="d-flex justify-content-between align-items-center ">
                      <Card.Title>{"Error Notifications"}</Card.Title>
                      <div className="d-flex align-items-center gap-2">
                            <div title={downloading ? 'Preparing report...' : 'Export'}>
                                <button className="btn btn-secondary h-100 d-flex align-items-center gap-1" onClick={handleExport} disabled={downloading}
                                    style={{ position: 'relative', padding: downloading ? '0.39rem 0.65rem' : '0.4rem 0.65rem' }}
                                >
                                    {downloading ? (
                                        
                                        <span className="spinner-border spinner-border-sm custom-spinner" role="status" aria-hidden="true"></span>
                                    ) : (
                                        <Download size={16} />
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>  
                </Card.Header>
                <CardBody>
                  { /* Need to build a generic pagination Start */ }   
                       <div className="d-flex flex-wrap align-items-center justify-content-between m-2">
                            <div className="d-flex flex-row gap-2 mt-3">
                                <h6 className="card-subtitle text-muted">Total Records: {totalRecords}</h6>
                                <h6 className="card-subtitle text-muted">Current Page : [ { pageNumber + 1 } of {totalPages} ]</h6>
                            </div>

                            <div className="d-flex align-items-center gap-3">
                                {/* // Page Size */}
                                <nav aria-label="">
                                    <ul className="pagination m-0">
                                        <li className="page-item">
                                            <select className="page-link" 
                                                value={pageSize}
                                                onChange={handlePageSizeChange}
                                                style={{width: '100px', textAlign: 'center', padding: '10px'}}>
                                                <option defaultChecked value={pageSize}>{pageSize}</option>
                                                <option value={25}>25</option>
                                                <option value={50}>50</option>
                                                <option value={75}>75</option>
                                                <option value={100}>100</option>
                                                <option value={150}>150</option>
                                                <option value={200}>200</option>
                                                <option value={250}>250</option>
                                                <option value={500}>500</option>
                                                <option value={750}>750</option>
                                                <option value={1000}>1000</option>
                                            </select>
                                        </li>                            
                                    </ul>
                                </nav>

                                {/* // Pagination Control */}
                                <nav aria-label="">
                                    <ul className="pagination m-0">
                                    <li className={`page-item ${pageNumber === 0 ? 'disabled' : ''}`}>
                                        <button 
                                        className="page-link d-flex align-items-center justify-content-center gap-1"
                                        onClick={goToFirstPage}
                                        disabled={pageNumber === 0}
                                        style={{ height: '38px', marginTop: '1px' }}
                                        >
                                        <ChevronsLeft size={18} />
                                        </button>
                                    </li>

                                    <li className={`page-item ${pageNumber <= 0 ? 'disabled' : ''}`}>
                                        <button 
                                        className="page-link d-flex align-items-center justify-content-center"
                                        onClick={goToPreviousPage}
                                        disabled={pageNumber <= 0}
                                        style={{ height: '38px', marginTop: '1px' }}
                                        >
                                        <ChevronLeft size={18} />
                                        </button>
                                    </li>

                                    <li className="page-item">
                                        <span
                                        className="page-link d-flex align-items-center justify-content-center"
                                        style={{ height: '38px', marginTop: '1px', padding: '0 14px' }}
                                        >
                                        Page {pageNumber + 1} of {totalPages}
                                        </span>
                                    </li>

                                    <li className={`page-item ${pageNumber >= totalPages - 1 ? 'disabled' : ''}`}>
                                        <button 
                                        className="page-link d-flex align-items-center justify-content-center"
                                        onClick={goToNextPage}
                                        disabled={pageNumber >= totalPages - 1}
                                        style={{ height: '38px', marginTop: '1px' }}
                                        >
                                        <ChevronRight size={18} />
                                        </button>
                                    </li>

                                    <li className={`page-item ${pageNumber >= totalPages - 1 ? 'disabled' : ''}`}>
                                        <button 
                                        className="page-link d-flex align-items-center justify-content-center gap-1"
                                        onClick={goToLastPage}
                                        disabled={pageNumber >= totalPages - 1}
                                        style={{ height: '38px', marginTop: '1px' }}
                                        >
                                        <ChevronsRight size={18} />
                                        </button>
                                    </li>
                                    </ul>
                                </nav>
                            </div>
                        </div>  
               { /* Need to build a generic pagination END */ }  
               <div className="sa-table-container" >
                <table className="table table-bordered sa-table">
                    <>
                     <thead className="sa-list-search">
                     <tr>
                            <th style={thStyle} className="sa-search-control"></th>                                    
                            <th style={thStyle} className="sa-search-control">
                                <input  id={`reference`}
                                        name="reference" 
                                        value={searchState.reference} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Referene...`} 
                                        className="form-control"
                                        >
                                </input>
                            </th>                            
                            <th style={thStyle} className="sa-search-control">
                                <input  id={`category`}
                                        name="category" 
                                        value={searchState.category} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Category...`} 
                                        className="form-control"
                                        >
                                </input>
                            </th>
                            <th style={thStyle} className="sa-search-control">
                                <input  id="system"
                                        name="system" 
                                        value={searchState.system} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search System...`} 
                                        className="form-control"
                                        >
                                </input>
                            </th>
                            <th style={thStyle} className="sa-search-control">
                                <input  id="function_code"
                                        name="function_code" 
                                        value={searchState.function_code} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Function Code...`} 
                                        className="form-control"
                                       >
                                </input>
                            </th>
                            <th style={thStyle} className="sa-search-control">
                                <input  id="description"
                                        name="description" 
                                        value={searchState.description} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Description...`} 
                                        className="form-control">
                                </input>
                            </th>
                            <th style={thStyle} className="sa-search-control">
                                <input  id="created"
                                        name="created" 
                                        value={searchState.created} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Created...`} 
                                        className="form-control">
                                </input>
                            </th>
                            <th style={thStyle} className="sa-search-control">
                                <input  id={`last_encountered`}
                                        name="last_encountered" 
                                        value={searchState.last_encountered} 
                                        onChange={handleSearchOnChange} 
                                        type="text" 
                                        autoComplete="off" 
                                        placeholder={`Search Last Encountered...`} 
                                        className="form-control"
                                        >
                                </input>
                            </th>
                            <th style={thStyle}>
                                    <div className="d-flex justify-content-end gap-1 w-100">
                                    <button
                                        type="button"
                                        className="btn btn-outline-danger btn-md d-flex justify-content-center align-items-center"
                                        onClick={searchClicked}
                                    >
                                    <Search style={{ width: '21px', height: '21px' }} strokeWidth={2} />
                                    </button>
                                    <button
                                        type="button"
                                        className="btn btn-outline-secondary btn-md d-flex justify-content-center align-items-center"
                                        onClick={clearClicked}
                                    >
                                    <X style={{ width: '21px', height: '21px' }} strokeWidth={2} />
                                    </button>
                                </div>
                            </th>
                        </tr> 
                     </thead>
                    </>

                    <>
                    <thead className="sa-list-columns">
                    <tr>
                            <th style={thStyle}>Occurrences</th>
                            <th style={thStyle}>Reference</th>
                            <th style={thStyle}>Category</th>
                            <th style={thStyle}>System</th>
                            <th style={thStyle}>FunctionCode</th>
                            <th style={thStyle}>Description</th>
                            <th style={thStyle}>Created</th>
                            <th style={thStyle}>Last Encountered</th>
                            <th style={thStyle}>
                                <div className="form-check me-2">
                                <>
                                    <div>
                                        <input
                                            type="checkbox"
                                            id="handled"
                                            name="handled"
                                            className="form-check-input"
                                            checked={isHandled}
                                            onChange={handleQueryHandledChange}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="handled"
                                        >
                                        Handled
                                        </label>
                                    </div>
                                    <div>
                                        <input
                                            type="checkbox"
                                            id="infrastructure"
                                            name="infrastructure"
                                            className="form-check-input"
                                            checked={showSystemNotifications} // Make sure you update this if needed.
                                            onChange={handleQuerySystemNotificationsChange}
                                        />
                                        <label
                                            className="form-check-label"
                                            htmlFor="infrastructure"
                                        >
                                        System Notifications
                                        </label>
                                    </div>
                                    </>
                                </div>                                                                     
                            </th>
                        </tr> 
                    </thead>
                    </>
                    <tbody>
                        {tableRows}
                    </tbody>
                </table>  
                </div>
                </CardBody>
            </Card>

            {/* Modal structure */}
            <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
                <Modal.Header closeButton>
                <Modal.Title>Doc Num: {notifications[currentRecordIndex]?.reference ? notifications[currentRecordIndex]?.reference : "N/A"}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <JsonToHtmlTable jsonObj={notifications[currentRecordIndex]} />
                </Modal.Body>
                <Modal.Footer>
                <Button variant="secondary" onClick={() => setShowModal(false)}>
                    Close
                </Button>
                </Modal.Footer>
            </Modal>

        </Container>
    );
}